import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, Play, Loader2, Zap, ShieldAlert } from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { checkTradingHours, smartInterval } from "@/lib/timeUtils";

export default function Signals() {
  const [selectedStock, setSelectedStock] = useState("");
  const { data: stockList } = trpc.data.stockList.useQuery();
  const { data: signals, refetch: refetchSignals } = trpc.trading.signals.useQuery();
  const _isTH = checkTradingHours().isTradingHours;
  const { data: cbStatus } = trpc.trading.circuitBreaker.useQuery(undefined, { refetchInterval: smartInterval(30000, _isTH, 120000) });

  const generateSignals = trpc.trading.generateSignals.useMutation({
    onSuccess: (data) => {
      refetchSignals();
      toast.success(`生成 ${data.length} 个交易信号`);
    },
    onError: (err) => toast.error(`信号生成失败: ${err.message}`),
  });

  const executeSignal = trpc.trading.executeSignal.useMutation({
    onSuccess: (result) => {
      refetchSignals();
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    },
    onError: (err) => toast.error(err.message),
  });

  const stocks = useMemo(() => stockList?.stocks ?? [], [stockList]);

  const selectedStockInfo = useMemo(() => {
    return stocks.find((s: any) => s.code === selectedStock);
  }, [stocks, selectedStock]);

  const signalTypeLabel = (t: string) => {
    switch (t) {
      case "buy": return { label: "买入", color: "bg-gain text-gain" };
      case "sell": return { label: "卖出", color: "bg-loss text-loss" };
      case "hold": return { label: "持有", color: "bg-muted text-muted-foreground" };
      case "stop": return { label: "停止", color: "bg-destructive text-destructive-foreground" };
      default: return { label: t, color: "bg-muted" };
    }
  };

  const sourceLabel = (s: string) => {
    switch (s) {
      case "bollinger": return "布林带";
      case "circuit_breaker": return "熔断开关";
      case "take_profit": return "止盈";
      case "stop_loss": return "止损";
      default: return s;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            交易信号监控
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            基于布林带策略生成买卖信号，支持一键执行
          </p>
        </div>
      </div>

      {/* 熔断状态 */}
      {cbStatus?.active && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="p-4 flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-destructive shrink-0" />
            <div>
              <p className="font-semibold text-destructive">大盘熔断已触发</p>
              <p className="text-sm text-muted-foreground">
                上证指数跌幅 {cbStatus.indexChange.toFixed(2)}%，所有买入信号已暂停
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 生成信号 */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">生成交易信号</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Select value={selectedStock} onValueChange={setSelectedStock}>
              <SelectTrigger className="w-[280px]">
                <SelectValue placeholder="选择股票" />
              </SelectTrigger>
              <SelectContent>
                {stocks.map((s: any) => (
                  <SelectItem key={s.code} value={s.code}>
                    {s.code} {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={() => {
                if (!selectedStock || !selectedStockInfo) {
                  toast.error("请先选择股票");
                  return;
                }
                generateSignals.mutate({
                  stockCode: selectedStock,
                  stockName: selectedStockInfo.name,
                });
              }}
              disabled={generateSignals.isPending || !selectedStock}
            >
              {generateSignals.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Zap className="h-4 w-4 mr-2" />
              )}
              生成信号
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 信号列表 */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">
            最近信号 ({signals?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {signals && signals.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-xs">代码</TableHead>
                    <TableHead className="text-xs">名称</TableHead>
                    <TableHead className="text-xs">信号</TableHead>
                    <TableHead className="text-xs">来源</TableHead>
                    <TableHead className="text-xs text-right">价格</TableHead>
                    <TableHead className="text-xs text-right">数量</TableHead>
                    <TableHead className="text-xs">布林带</TableHead>
                    <TableHead className="text-xs">原因</TableHead>
                    <TableHead className="text-xs">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {signals.map((sig) => {
                    const st = signalTypeLabel(sig.signalType);
                    return (
                      <TableRow key={sig.id} className="border-border/30">
                        <TableCell className="text-xs font-mono">{sig.stockCode}</TableCell>
                        <TableCell className="text-xs">{sig.stockName}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${st.color}`}>{st.label}</Badge>
                        </TableCell>
                        <TableCell className="text-xs">{sourceLabel(sig.signalSource)}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{sig.price?.toFixed(2)}</TableCell>
                        <TableCell className="text-xs text-right">{sig.quantity}</TableCell>
                        <TableCell className="text-xs">
                          <span className="text-loss">{sig.bbLower?.toFixed(1)}</span>
                          {" / "}
                          <span>{sig.bbMiddle?.toFixed(1)}</span>
                          {" / "}
                          <span className="text-gain">{sig.bbUpper?.toFixed(1)}</span>
                        </TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate" title={sig.reason || ""}>
                          {sig.reason}
                        </TableCell>
                        <TableCell>
                          {sig.executed === 0 && (sig.signalType === "buy" || sig.signalType === "sell") && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => {
                                executeSignal.mutate({
                                  signalId: sig.id,
                                  stockCode: sig.stockCode,
                                  stockName: sig.stockName,
                                  direction: sig.signalType as "buy" | "sell",
                                  price: sig.price,
                                  quantity: sig.quantity,
                                });
                              }}
                              disabled={executeSignal.isPending}
                            >
                              {executeSignal.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "执行"}
                            </Button>
                          )}
                          {sig.executed === 1 && (
                            <Badge variant="secondary" className="text-[10px]">已执行</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Activity className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">选择股票并生成交易信号</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
